import React from "react";
import Cta from "@qlik/qlik/src/components/Cta";
import Media from "@qlik/qlik/src/components/Media";
import { getSpacingClassName } from "@qlik/qlik/src/lib/util/spacing";
import { getColorClassName } from "@qlik/qlik/src/lib/util/colors";
import { getCardBodyContent, renderSanitizedHtmlContent } from "@qlik/qlik/src/lib/util/helpers";
import * as styles from "./styles.module.css";

/**
 * HeroBannerImage component.
 * Renders a section with a title, eyebrowText, body, CTA, and media.
 * @param {Object} data - The data for the component.
 * @param {Object} data.cardItem - The card item data.
 * @param {Object} data.additionalConfiguration - The additional configuration data.
 * @param {Object} data.backgroundColor - The background color data.
 */
const HeroBannerImage = ({ data }) => {
    const { additionalConfiguration, cardItem, backgroundColor, customId } = data;
    const { title, eyebrowText, cta, media } = cardItem ?? {};

    const bodyContent = getCardBodyContent(cardItem);

    // Check if media assets exist
    const mediaAssetExist = media?.length > 0;

    const additionalConfigurationClass = getSpacingClassName(additionalConfiguration);
    const { sectionColorMapping } = getColorClassName(backgroundColor);

    // eslint-disable-next-line no-nested-ternary
    const heroBannerColumnClass = mediaAssetExist
        ? "w-full lg:w-1/2"
        : !additionalConfigurationClass
        ? "w-full xl:py-20"
        : "";
    const heroBannerSectionHeight = mediaAssetExist ? "xl:min-h-[550px]" : "";
    // eslint-disable-next-line no-nested-ternary
    const heroBannerContentPadding = mediaAssetExist
        ? "pt-10 pb-7.5 lg:py-10 pr-3.75 md:pr-10 xl:pr-[47px]"
        : !additionalConfigurationClass
        ? "py-10"
        : "";

    const eyebrowTextColor = sectionColorMapping.bgColor ? sectionColorMapping.primaryTextColor : "text-ocean";
    const titleColor = sectionColorMapping.bgColor ? sectionColorMapping.primaryTextColor : "";
    const bodyColor = sectionColorMapping.bgColor ? styles.descriptionLight : styles.description;

    return (
        <section
            className={`overflow-hidden ${heroBannerSectionHeight} ${additionalConfigurationClass} ${sectionColorMapping.bgColor}`}
            id={customId}
        >
            <div className={`container ${heroBannerSectionHeight}`}>
                <div className={`row ${heroBannerSectionHeight}`}>
                    <div
                        className={`${heroBannerColumnClass} ${heroBannerContentPadding}  self-center text-center lg:text-left`}
                    >
                        {eyebrowText && (
                            <p
                                className={`text-sm lg:text-base font-medium tracking-[3.5px] lg:tracking-wide5 leading-none lg:leading-tight mb-3.75 ${eyebrowTextColor}`}
                                // eslint-disable-next-line react/no-danger
                                dangerouslySetInnerHTML={{ __html: renderSanitizedHtmlContent(eyebrowText) }}
                            />
                        )}
                        {/* Rendering the title */}
                        <h1
                            className={`title-2 text-with-color ${titleColor}`}
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{ __html: renderSanitizedHtmlContent(title) }}
                        />
                        {/* Rendering the description if it exists */}
                        {bodyContent && <div className={`${styles.bodyContent} ${bodyColor}`}>{bodyContent}</div>}
                        {/* Rendering the CTA if it exists */}
                        {cta?.length > 0 && (
                            <div className="mt-2.5 flex flex-col lg:flex-row lg:flex-wrap items-center gap-x-5 lg:mt-7.5 lg:justify-start">
                                {cta?.map((button) => (
                                    <Cta key={button.id} data={button} className="mt-5" />
                                ))}
                            </div>
                        )}
                    </div>
                    {/* Rendering the media if it exists */}
                    {mediaAssetExist && (
                        <div className={`${heroBannerColumnClass} relative`}>
                            <div className="pt-4 sm:pt-0 xl:absolute -mx-10 sm:mx-auto top-0 w-auto xl:w-[704px] h-full">
                                {media?.[0] && (
                                    <Media
                                        options={{ loop: false }}
                                        media={media?.[0]}
                                        className="object-contain xl:object-cover w-full h-full"
                                        videoClassName="object-contain xl:object-cover w-full h-full"
                                    />
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

export default HeroBannerImage;
